import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { loadEmployeeGroups } from '../../employee-groups/actions/load-employee-groups';
import { employeeGroupsForOrgIdSelector } from '../../employee-groups/employee-group-selectors';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { userSelector } from '../../reducers/current-user-selectors';
import { employeesForOrgIdSelector } from '../../reducers/employee-selectors';

import { UiButton } from '../../ui/ui-button';

import { EmployeeGroupSendAnnouncement } from './employee-group-send-announcement';
import { LegacyEmployeeGroupAnnouncements } from './legacy-employee-group-announcements';

import { getEmployeeGroupsFromEmployees } from '../../employees/utils/get-employee-groups-from-employees';
import { allEmployeesKey, allEmployeesLabel } from '../../util/all-employees-option';
import { LoadingMessage } from '../../widgets/loading-message';

const mapStateToProps = (state: any, { orgId }: InputProps) => {
  const currentEmployees = (employeesForOrgIdSelector(state, orgId) || []).filter(
    (emp) => emp.isCurrentEmployee,
  );
  const allEmployeeGroups = employeeGroupsForOrgIdSelector(state, orgId);

  return {
    employeeGroups: getEmployeeGroupsFromEmployees(currentEmployees, allEmployeeGroups),
    user: userSelector(state),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(orgId)),
    loadCurrentEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
  },
});

type InputProps = {
  orgId: number;
  onClose: () => void;
  onChange?: (employeeGroup: EmployeeGroupWithAllOption | null) => void;
  defaultToAllEmployees?: boolean;
  autoFocus?: boolean;
  hideThread?: boolean;
  className?: string;
  initialMessage?: string;
  onMessageSent?: () => void;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const LegacyAnnouncementComposer = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    orgId,
    employeeGroups,
    onChange,
    onClose,
    actions,
    defaultToAllEmployees,
    autoFocus,
    className,
    initialMessage,
    onMessageSent,
    user,
    hideThread,
  } = props;
  const allEmployeesOption = {
    employeeGroupId: allEmployeesKey(orgId),
    name: allEmployeesLabel(user),
    orgId,
  };
  const [activeEmployeeGroup, setActiveEmployeeGroup] = useState<EmployeeGroupWithAllOption | null>(
    defaultToAllEmployees ? allEmployeesOption : null,
  );

  useEffect(() => {
    actions.loadOrgEmployeeGroups();
    actions.loadCurrentEmployees();
  });

  if (!employeeGroups) {
    return <LoadingMessage />;
  }

  const groupsAndAllEmployees = [allEmployeesOption, ...employeeGroups];

  const handleGroupChange = (
    _e: React.ChangeEvent<{}>,
    employeeGroup: EmployeeGroupWithAllOption | null,
  ) => {
    setActiveEmployeeGroup(employeeGroup);
    if (onChange) {
      onChange(employeeGroup);
    }
  };

  const renderThread = (employeeGroupId: number) => (
    <div className="u-flexGrow-1 u-overflowY-scroll u-padding u-no-padding-top">
      <div className="u-maxWidth-800px u-margin-0Auto">
        <LegacyEmployeeGroupAnnouncements
          onMessageSent={onMessageSent}
          initialMessage={initialMessage}
          orgId={orgId}
          employeeGroupId={employeeGroupId}
          onClose={() => {}}
        />
      </div>
    </div>
  );

  const renderComposerOnly = (employeeGroupId: number) => (
    <div className="u-padding">
      <EmployeeGroupSendAnnouncement
        initialMessage={initialMessage}
        onMessageSent={onMessageSent}
        orgId={orgId}
        employeeGroupId={employeeGroupId}
        onClose={onClose}
      />
    </div>
  );

  const renderEmptyState = () => (
    <div className="u-display-flex u-flexGrow-1 u-alignItems-center u-justifyContent-center u-padding-2x">
      <div className="u-display-flex u-flexDirection-column u-alignItems-center">
        <div className="typo-h6">Select a group to send an announcement</div>
        <UiButton variant="dismiss" onClick={onClose} dataCy="empty-state-cancel">
          Cancel
        </UiButton>
      </div>
    </div>
  );

  return (
    <div
      className={`u-display-flex u-flexDirection-column u-height-100 u-overflowY-auto ${
        className || ''
      }`}
    >
      <div
        className="u-spread u-padding u-margin-bottom-md"
        style={{ borderBottom: '1px solid #e0e0e0' }}
      >
        <div className="typo-title u-flexShrink-0 u-margin-right">Send To: </div>
        <Autocomplete
          defaultValue={defaultToAllEmployees ? allEmployeesOption : undefined}
          autoHighlight
          fullWidth
          openOnFocus={autoFocus}
          options={groupsAndAllEmployees}
          getOptionLabel={(option) => option.name}
          onChange={handleGroupChange}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Employee group"
              autoFocus={autoFocus}
              fullWidth
              inputProps={{ 'data-cy': 'employee-group-autocomplete', ...params.inputProps }}
            />
          )}
        />
      </div>
      {activeEmployeeGroup &&
        !hideThread &&
        renderThread(activeEmployeeGroup.employeeGroupId as number)}
      {activeEmployeeGroup &&
        hideThread &&
        renderComposerOnly(activeEmployeeGroup.employeeGroupId as number)}
      {!activeEmployeeGroup && renderEmptyState()}
    </div>
  );
});
